import { useMutation } from '@tanstack/react-query';
import { Button, Label, Modal, type ModalProps,Textarea, TextInput } from 'flowbite-react';
import { CheckIcon, LifeBuoyIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';

interface FormValues {
    school: string;
    faculty: string;
    message: string;
}

export interface MissingTemplateModalProps extends ModalProps {
    locationInApp: string;
}

export const MissingTemplateModal = ({
    locationInApp,
    show,
    onClose
}: MissingTemplateModalProps) => {
    const api = useApi();

    const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
        defaultValues: {
            message: ''
        }
    });

    const submitFeedback = useMutation({
        mutationFn: (data: FormValues) => api.v1.productSupport.submitFeedback({
            category: 'Chybějící šablona',
            documentId: null,
            documentSection: null,
            message: data.message,
            fields: {
                'Škola': data.school,
                'Fakulta': data.faculty
            },
            locationInApp
        })
    });

    const onSubmit = async (data: FormValues) => {
        submitFeedback.mutate(data);
    };

    return (
        <Modal show={show} size="3xl" onClose={onClose} popup>
            <Modal.Header>
                <span className="flex flex-row items-start md:items-center leading-5 justify-center gap-2 p-3 text-base md:text-lg font-semibold text-gray-800 dark:text-gray-400">
                    <LifeBuoyIcon size={20} />
                    <span>Chybějící šablona</span>
                </span>
            </Modal.Header>
            <Modal.Body>
                {!submitFeedback.isSuccess && (
                    <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-4'>
                        <div>
                            <Label htmlFor="school" className="block text-sm font-medium text-gray-700">Vaše škola</Label>
                            <TextInput
                                {...register('school', {
                                    required: 'Musíte vyplnit Vaši školu.',
                                    maxLength: { value: 1000, message: 'Maximální délka je 1 000 znaků' }
                                })}
                                color={errors.school ? 'failure' : 'default'}
                                disabled={submitFeedback.isPending || submitFeedback.isSuccess}
                            />
                            {errors.school && (
                                <span className="block text-sm text-red-500 mt-1">
                                    {errors.school.message}
                                </span>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="faculty" className="block text-sm font-medium text-gray-700">Vaše fakulta</Label>
                            <TextInput
                                {...register('faculty', {
                                    maxLength: { value: 1000, message: 'Maximální délka je 1 000 znaků' }
                                })}
                                color={errors.faculty ? 'failure' : 'default'}
                                disabled={submitFeedback.isPending || submitFeedback.isSuccess}
                            />
                            {errors.faculty && (
                                <span className="block text-sm text-red-500 mt-1">
                                    {errors.faculty.message}
                                </span>
                            )}
                        </div>

                        <div>
                            <Label htmlFor="message" className="block mb-1">Doplňující informace</Label>
                            <Textarea
                                id="message"
                                placeholder='Napište nám, jak Vám můžeme pomoci.'
                                {...register('message', {
                                    maxLength: { value: 30000, message: 'Maximální délka je 30 000 znaků' }
                                })}
                                rows={4}
                                color={errors.message ? 'failure' : 'default'}
                                helperText={(
                                    <>
                                        {errors.message && (
                                            <span className='block'>
                                                {errors.message.message}
                                            </span>
                                        )}
                                    </>
                                )}
                                disabled={submitFeedback.isPending || submitFeedback.isSuccess}
                            />
                            <p className='text-sm text-gray-500 mt-1'>Šablonu doplníme do 24 hodin nebo se Vám individuálně ozveme.</p>
                        </div>
                    
                        <div className='flex flex-row items-center justify-end gap-2'>
                            <ErrorSwitch
                                error={submitFeedback.error}
                                default={{ message: 'Při odesílání dat došlo k chybě. Zkuste to prosím za chvíli znovu.', showDetails: true }}
                                options={[]}
                                className='grow'
                            />

                            <Button
                                type='submit'
                                color='primary'
                                disabled={submitFeedback.isPending}
                            >Odeslat zprávu</Button>

                            <Button
                                color='gray'
                                onClick={onClose}
                            >Zavřít</Button>
                        </div>
                    </form>
                )}

                {submitFeedback.isSuccess && (
                    <div className='flex flex-col gap-4'>
                        <div className='flex flex-row items-center justify-center gap-2 p-4 border border-gray-300 rounded-lg'>
                            <CheckIcon size={32} className='text-lime-600' />
                            <p className='font-medium text-lime-700'>Vaše zpráva byla odeslána. Budeme Vás co nejdříve kontaktovat zpět.</p>
                        </div>

                        <div className='flex flex-row items-center justify-center gap-2 flex-wrap'>
                            <Button
                                color='primary'
                                onClick={onClose}
                            >Zavřít</Button>
                        </div>
                    </div>
                )}
            </Modal.Body>
        </Modal>
    );
};
